import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { FilePenLineIcon, FileSearch, Info } from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";

import { UPCLAIM_ROLES } from "@upclaim/sdk/src/admin-roles";

import { FilePreviewDialog, type PreviewFile } from "@/components/files/file-preview-dialog";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useCurrentUser } from "@/hooks/use-current-user";

import type { ClaimWithRelations } from "../api/use-claim";
import { type ReimbursalStatus, useUpdateExpenseReimbursal } from "../api/use-expense-mutations";
import { expenseTypesMap } from "../lib/expense-types";
import { type AmountWithConversions, formatAmountWithConversion } from "../lib/money";
import { ReimbursalStatusSelect } from "./reimbursal-status-select";
import { UpsertExpenseDialog } from "./upsert-expense-dialog";

export type ClaimExpenseItem = ClaimWithRelations["claimExpenses"][number];

interface ExpensesListProps {
  claim: ClaimWithRelations;
  claimExpenses: ClaimExpenseItem[];
  showActions?: boolean;
  header?: ReactNode;
}

function centsFromExpense(expense: ClaimExpenseItem["expense"]): number {
  if (expense.amount) {
    return expense.amount.amount;
  }
  // `price` est déjà en centimes (cf. `price / 100` à l'affichage).
  const parsed = Number.parseFloat(String(expense.price));
  return Number.isNaN(parsed) ? 0 : Math.round(parsed);
}

function centsFromReimbursed(expense: ClaimExpenseItem["expense"]): number {
  return expense.reimbursedAmount?.amount ?? 0;
}

function currentStatus(expense: ClaimExpenseItem["expense"]): ReimbursalStatus {
  return expense.reimbursalStatus ?? "PENDING";
}

/** Port of Expenses.vue — list + create/edit/delete + reimbursal status actions. */
export function ExpensesList({ claim, claimExpenses, showActions, header }: ExpensesListProps) {
  const { can } = useCurrentUser();
  const updateReimbursal = useUpdateExpenseReimbursal(claim.id);

  const [previewedFile, setPreviewedFile] = useState<PreviewFile | null>(null);
  const [upsertOpen, setUpsertOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<ClaimExpenseItem | null>(null);
  const [savingExpenseId, setSavingExpenseId] = useState<number | null>(null);

  if (!claimExpenses.length && !showActions) {
    return null;
  }

  function openCreate() {
    setSelectedExpense(null);
    setUpsertOpen(true);
  }

  function openEdit(expenseItem: ClaimExpenseItem) {
    setSelectedExpense(expenseItem);
    setUpsertOpen(true);
  }

  function previewDocument(expense: ClaimExpenseItem["expense"]) {
    const file = expense.claimDocument?.file;
    if (!file) {
      return;
    }
    setPreviewedFile({
      id: file.id,
      name: file.name ?? null,
      mimeType: file.mimeType ?? null,
      size: file.size ?? null,
      boxFileId: file.boxFileId ?? null,
    });
  }

  /** Mirrors Expenses.saveReimbursal (Vue). */
  async function saveReimbursal(
    expenseItem: ClaimExpenseItem,
    nextStatus: ReimbursalStatus,
    reimbursedAmountCents?: number,
  ) {
    setSavingExpenseId(expenseItem.expense.id);
    try {
      await updateReimbursal.mutateAsync({
        expenseId: expenseItem.expense.id,
        status: nextStatus,
        reimbursedAmountCents,
      });
    } catch {
      // Error toasts handled by notify.promise.
    } finally {
      setSavingExpenseId(null);
    }
  }

  function onStatusChange(expenseItem: ClaimExpenseItem, nextStatus: ReimbursalStatus) {
    if (nextStatus === "REIMBURSED" || nextStatus === "PARTIAL") {
      const existing = centsFromReimbursed(expenseItem.expense);
      const fallback = nextStatus === "REIMBURSED" ? centsFromExpense(expenseItem.expense) : existing;
      void saveReimbursal(expenseItem, nextStatus, fallback);
      return;
    }
    void saveReimbursal(expenseItem, nextStatus);
  }

  function onAmountBlur(expenseItem: ClaimExpenseItem, rawValue: string) {
    const parsed = Number.parseFloat(rawValue.replace(",", "."));
    if (Number.isNaN(parsed) || parsed < 0) {
      return;
    }
    const cents = Math.round(parsed * 100);
    const existingCents = centsFromReimbursed(expenseItem.expense);
    if (cents === existingCents) {
      return;
    }
    const originalCents = centsFromExpense(expenseItem.expense);
    const nextStatus: ReimbursalStatus =
      cents === 0 ? "PENDING" : cents >= originalCents ? "REIMBURSED" : "PARTIAL";
    void saveReimbursal(expenseItem, nextStatus, cents === 0 ? undefined : cents);
  }

  return (
    <div className="py-6 sm:px-6">
      {header}

      <UpsertExpenseDialog
        open={upsertOpen}
        onOpenChange={(open) => {
          setUpsertOpen(open);
          if (!open) {
            setSelectedExpense(null);
          }
        }}
        claim={claim}
        existingExpense={selectedExpense}
      />

      <FilePreviewDialog
        open={previewedFile !== null}
        file={previewedFile}
        onClose={() => setPreviewedFile(null)}
      />

      <div className="pt-4">
        {claimExpenses.length ? (
          <ul className="col-span-3 w-full divide-y divide-gray-100 rounded-md border border-gray-300 bg-white shadow-sm">
            {claimExpenses.map((expenseItem) => {
              const { expense } = expenseItem;
              const ExpenseIcon = expenseTypesMap[expense.expenseType.type]?.icon ?? Info;
              const saving = savingExpenseId === expense.id;

              return (
                <li key={expense.id} className="py-4 pr-5 pl-4 text-sm leading-6">
                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      className="flex w-0 flex-1 cursor-pointer items-center text-left"
                      onClick={() => previewDocument(expense)}
                    >
                      <span className="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true">
                        <ExpenseIcon size={20} />
                      </span>
                      <div className="ml-4 flex min-w-0 flex-1 gap-2">
                        <span className="truncate font-medium">
                          {expense.expenseName ||
                            expense.claimDocument?.file?.name ||
                            "Dépense supplémentaire"}
                        </span>
                      </div>
                    </button>
                    <div className="ml-4 flex flex-shrink-0 items-center space-x-4">
                      {expense.claimDocument ? (
                        <Tooltip>
                          <TooltipTrigger
                            render={
                              <button
                                type="button"
                                onClick={() => previewDocument(expense)}
                                className="rounded-md font-medium text-gray-900 hover:text-gray-800"
                                aria-label="Voir le justificatif"
                              />
                            }
                          >
                            <FileSearch className="h-5 w-5" />
                          </TooltipTrigger>
                          <TooltipContent>
                            Ajouté le{" "}
                            {format(new Date(expense.claimDocument.createdAt), "d MMMM 'à' HH'h'mm", {
                              locale: fr,
                            })}
                          </TooltipContent>
                        </Tooltip>
                      ) : null}
                      {expense.claimDocument ? (
                        <span className="text-gray-200" aria-hidden="true">
                          |
                        </span>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => openEdit(expenseItem)}
                        className="rounded-md font-medium text-blue-600 hover:text-blue-500"
                        aria-label="Modifier la dépense"
                      >
                        <FilePenLineIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 pl-9 text-xs">
                    <span className="text-gray-500 italic">
                      Payé par{" "}
                      {expenseItem.userClaimId
                        ? expenseItem.paidBy
                        : expenseItem.paidBy === "TRAVEL_AGENCY"
                          ? "l'agence de voyages"
                          : "l'entreprise"}
                    </span>
                    {!expense.claimDocument ? (
                      <span className="text-amber-600">· Justificatif manquant</span>
                    ) : null}
                    <div className="flex items-center gap-1.5">
                      <span className="inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 font-medium text-gray-900 ring-1 ring-gray-200 ring-inset">
                        <svg
                          className={`h-1.5 w-1.5 ${expense.claimDocument ? "fill-blue-500" : "fill-amber-500"}`}
                          viewBox="0 0 6 6"
                          aria-hidden="true"
                        >
                          <circle cx="3" cy="3" r="3" />
                        </svg>
                        {expense.amount
                          ? formatAmountWithConversion(expense.amount as AmountWithConversions)
                          : `${(Number.parseInt(expense.price, 10) / 100).toFixed(2)} ${expense.currencyCode}`}
                      </span>
                      {expense.claimMonetaryDemandId && can(UPCLAIM_ROLES) ? (
                        <>
                          <span className="text-gray-400">→</span>
                          <div className="flex items-center gap-1.5">
                            <div className="relative">
                              <input
                                key={`${expense.id}-${centsFromReimbursed(expense)}`}
                                type="text"
                                inputMode="decimal"
                                className="h-6 w-16 rounded-md border-0 bg-gray-50 px-1.5 pr-4 text-right text-xs font-medium text-gray-900 ring-1 ring-gray-200 ring-inset focus:bg-white focus:ring-2 focus:ring-blue-500"
                                defaultValue={(centsFromReimbursed(expense) / 100).toFixed(2)}
                                disabled={saving}
                                onBlur={(event) => onAmountBlur(expenseItem, event.currentTarget.value)}
                                aria-label="Montant remboursé"
                              />
                              <span className="pointer-events-none absolute inset-y-0 right-1 flex items-center text-xs text-gray-400">
                                €
                              </span>
                            </div>
                            <ReimbursalStatusSelect
                              value={currentStatus(expense)}
                              disabled={saving}
                              onChange={(status) => onStatusChange(expenseItem, status)}
                            />
                          </div>
                        </>
                      ) : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <ul className="col-span-3 mb-5 w-full divide-y divide-gray-100 rounded-md border border-dotted border-gray-300 bg-white shadow-sm">
            <li className="flex items-center justify-between py-4 pr-5 pl-4 text-sm leading-6">
              <div className="flex w-0 flex-1 items-center">
                <Info className="ml-2 h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
                <div className="ml-4 flex min-w-0 flex-1 gap-2">
                  <span className="flex items-center truncate font-medium">
                    Aucun préjudice pour l'instant
                  </span>
                  {showActions ? (
                    <div className="ml-auto">
                      <Button variant="outline" size="sm" onClick={openCreate}>
                        Ajouter une dépense
                      </Button>
                    </div>
                  ) : null}
                </div>
              </div>
            </li>
          </ul>
        )}

        {claimExpenses.length && showActions ? (
          <div className="mt-5 text-left">
            <Button variant="outline" onClick={openCreate}>
              Ajouter une dépense
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
